// /coming-soon — pre-launch waitlist surface for the Phase A landing
// ship. The Hero CTA "Step into the Awakening" still navigates to
// /awakening; on landing-only hostnames (anything other than the
// allowlist in `lib/landing-mode.ts`), /awakening and every other
// private route redirect here. Cold visitors from Instagram land on
// the landing first; if they engage with the CTA they meet this page.
//
// The form submits to a Cloudflare Pages Function at /api/waitlist
// (apps/web/functions/api/waitlist.ts) which appends the email to a
// Resend audience. Resend handles double-opt-in confirmation if the
// audience is configured for it.

import { createFileRoute, useSearch } from '@tanstack/react-router';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { BrandMark } from '../components/BrandMark';

// TanStack Router auto-parses all-digit search params as numbers
// (`?confirmed=1` → `{ confirmed: 1 }`), so a bare `z.string()` rejects
// the success redirect from /api/waitlist/confirm. Coerce to string so
// both the numeric flag and the textual `reason` survive validation.
const comingSoonSearch = z.object({
  confirmed: z.coerce.string().optional(),
  reason: z.coerce.string().optional(),
});

export const Route = createFileRoute('/coming-soon')({
  validateSearch: comingSoonSearch,
  component: ComingSoonPage,
});

type Phase = 'idle' | 'submitting' | 'success' | 'error';

function ComingSoonPage() {
  const { t, i18n } = useTranslation();
  const search = useSearch({ from: '/coming-soon' });
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [phase, setPhase] = useState<Phase>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // The confirm endpoint redirects back here with confirmed=1 on
  // success or confirmed=0 + a reason on failure. We render a
  // dedicated state for each instead of mixing the form into a
  // confirmation surface.
  const confirmation = (() => {
    if (search.confirmed === '1') return 'success' as const;
    if (search.confirmed === '0') {
      const reason = search.reason ?? '';
      if (reason === 'expired') return 'expired' as const;
      if (reason === 'gone') return 'gone' as const;
      return 'invalid' as const;
    }
    return null;
  })();

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (phase === 'submitting' || !consent || email.trim().length === 0) return;
    setPhase('submitting');
    setErrorMessage(null);
    try {
      // The current i18n language at submit time is what we want to
      // record alongside the email — that way Phase B can address
      // each waitlist member in their preferred locale when sending
      // the launch invite, regardless of where they happen to be when
      // they open the email.
      const locale = i18n.language?.startsWith('es') ? 'es' : 'en';
      const res = await fetch('/api/waitlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), consent: true, locale }),
      });
      if (res.ok) {
        setPhase('success');
        return;
      }
      const text = await res.text().catch(() => '');
      setErrorMessage(text || `${res.status}`);
      setPhase('error');
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'unknown');
      setPhase('error');
    }
  }

  if (confirmation) {
    const key =
      confirmation === 'success'
        ? 'confirmed'
        : confirmation === 'expired'
          ? 'expired'
          : confirmation === 'gone'
            ? 'gone'
            : 'invalid';
    return (
      <div className="flex min-h-[80vh] flex-col items-center justify-center text-center animate-fadeUp">
        <BrandMark />
        <div className="mt-12 max-w-xl space-y-5 font-body text-ash-200 sm:text-lg">
          <p className="font-display text-ash-100 text-3xl leading-snug sm:text-4xl">
            {t(`comingSoon.${key}.heading`)}
          </p>
          <p className="italic text-ash-300">{t(`comingSoon.${key}.body`)}</p>
        </div>
        <div className="hairline mt-16 h-px w-32" />
      </div>
    );
  }

  if (phase === 'success') {
    return (
      <div className="flex min-h-[80vh] flex-col items-center justify-center text-center animate-fadeUp">
        <BrandMark />
        <div className="mt-12 max-w-xl space-y-5 font-body text-ash-200 sm:text-lg">
          <p className="font-display text-ash-100 text-3xl leading-snug sm:text-4xl">
            {t('comingSoon.success.heading')}
          </p>
          <p className="italic text-ash-300">{t('comingSoon.success.body')}</p>
        </div>
        <div className="hairline mt-16 h-px w-32" />
      </div>
    );
  }

  return (
    <div className="flex min-h-[80vh] flex-col items-center justify-center text-center animate-fadeUp">
      <BrandMark />

      {/* Closed-door visual anchor. Sits between the brand mark and
       * the heading so the eye lands on it before reading. The
       * landing-ember-mask dissolves the rectangular frame; a soft
       * accent halo sits behind to tie the image into the page's
       * per-visit Ember palette. Decorative; pointer-events:none so
       * it never intercepts a click meant for the form. */}
      <figure
        className="relative mt-10 w-44 select-none animate-fadeUp sm:w-56 lg:w-64"
        style={{ animationDelay: '0.4s' }}
      >
        <div
          aria-hidden="true"
          className="-inset-6 absolute animate-softGlow rounded-full bg-gradient-radial from-accent/20 via-accent/5 to-transparent blur-2xl"
        />
        <img
          src="/coming-soon/door.png"
          alt=""
          aria-hidden="true"
          className="landing-ember-mask pointer-events-none relative w-full"
          draggable={false}
        />
      </figure>

      <div className="mt-8 max-w-xl space-y-5 font-body text-ash-200 sm:text-lg">
        <p className="font-display text-ash-100 text-3xl leading-snug sm:text-4xl">
          {t('comingSoon.heading')}
        </p>
        <p className="italic text-ash-300">{t('comingSoon.body')}</p>
      </div>

      <form onSubmit={handleSubmit} className="mt-12 w-full max-w-sm">
        <label htmlFor="waitlist-email" className="sr-only">
          {t('comingSoon.emailLabel')}
        </label>
        <input
          id="waitlist-email"
          type="email"
          required
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t('comingSoon.emailPlaceholder')}
          disabled={phase === 'submitting'}
          className="w-full border-b border-ash-700 bg-transparent pb-3 text-center font-body text-ash-100 text-lg placeholder:text-ash-500 focus:border-moonlit-400 focus:outline-none disabled:opacity-50"
        />

        <label
          htmlFor="waitlist-consent"
          className="mt-8 flex cursor-pointer items-start gap-3 text-left font-body text-ash-400 text-xs leading-relaxed sm:text-sm"
        >
          <input
            id="waitlist-consent"
            type="checkbox"
            required
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            disabled={phase === 'submitting'}
            className="mt-1 size-4 cursor-pointer accent-moonlit-400"
          />
          <span>
            {t('comingSoon.consentPrefix')}
            <a
              href="/privacy"
              className="text-ash-300 underline-offset-4 hover:text-moonlit-400 hover:underline"
            >
              {t('comingSoon.consentLink')}
            </a>
            {t('comingSoon.consentSuffix')}
          </span>
        </label>

        <button
          type="submit"
          disabled={phase === 'submitting' || !consent || email.trim().length === 0}
          className="mt-10 w-full border border-ash-700 px-8 py-3 font-display text-ash-200 text-sm uppercase tracking-[0.4em] transition hover:border-moonlit-400 hover:text-ash-100 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {phase === 'submitting' ? t('comingSoon.submitting') : t('comingSoon.submit')}
        </button>

        {phase === 'error' && (
          <p className="mt-6 font-body text-ember-400 text-sm" role="alert">
            {errorMessage
              ? t('comingSoon.errorWithDetail', { detail: errorMessage })
              : t('comingSoon.errorFallback')}
          </p>
        )}
      </form>

      <div className="hairline mt-16 h-px w-32" />

      <p className="mt-8 max-w-md font-body text-ash-500 text-xs italic leading-relaxed">
        {t('comingSoon.footnotePrefix')}
        <a
          href="https://instagram.com/athrelaris"
          target="_blank"
          rel="noreferrer"
          className="text-ash-300 underline-offset-4 hover:text-moonlit-400 hover:underline"
        >
          @athrelaris
        </a>
        {t('comingSoon.footnoteSuffix')}
      </p>
    </div>
  );
}
